// Call plugin.
import * as eventTypes from '../eventTypes'
import * as actionTypes from '../actionTypes'
import { OPERATIONS, OP_TRANSITIONS } from '../constants'
import { getCallById, getCallByWebrtcSessionId } from '../selectors'

// Webrtc plugin.
import * as webrtcActionTypes from '../../../webrtc/interface/actionTypes'

// Handlers.
import { callOperationHandler, callEventHandler, stateChangeHandler, trackRemovedHandler } from './handlers'

// Libraries.
import { without } from 'lodash/fp'

const callEvents = {}
/*
  "Misc" actions are:
    - SEND_DTMF
    - SEND_DTMF_FINISH

    - GET_STATS
    - GET_STATS_FINISH

    - MEDIA_RESTART
    - MEDIA_RESTART_FINISH

    - RESYNC
    - RESYNC_FINISH

    - SEND_CUSTOM_PARAMETERS
    - SEND_CUSTOM_PARAMETERS_FINISH
    - CUSTOM_PARAMETERS_RECEIVED

    - AVAILABLE_CODECS_RETRIEVED
    - PENDING_OPERATION
    - UPDATE_CALL

    - SESSION_ICE_CONNECTION_STATE_CHANGE
 */

callEvents[actionTypes.SEND_DTMF] = (action, params) => {
  return callOperationHandler(action, {
    ...params,
    operation: OPERATIONS.SEND_DTMF,
    transition: OP_TRANSITIONS.START,
    isLocal: true
  })
}

callEvents[actionTypes.SEND_DTMF_FINISH] = (action, params) => {
  return callOperationHandler(action, {
    ...params,
    operation: OPERATIONS.SEND_DTMF,
    transition: OP_TRANSITIONS.FINISH,
    isLocal: true
  })
}

// ----

callEvents[actionTypes.GET_STATS] = (action, params) => {
  return callOperationHandler(action, {
    ...params,
    operation: OPERATIONS.GET_STATS,
    transition: OP_TRANSITIONS.START,
    isLocal: true
  })
}

callEvents[actionTypes.GET_STATS_FINISH] = (action, params) => {
  return [
    callOperationHandler(action, {
      ...params,
      operation: OPERATIONS.GET_STATS,
      transition: OP_TRANSITIONS.FINISH,
      isLocal: true
    }),
    callEventHandler(eventTypes.STATS_RECEIVED, action, {
      result: action.payload.result,
      error: action.payload.error,
      trackId: action.payload.trackId
    })
  ]
}

// ----

callEvents[actionTypes.MEDIA_RESTART] = (action, params) => {
  return callOperationHandler(action, {
    ...params,
    operation: OPERATIONS.MEDIA_RESTART,
    transition: OP_TRANSITIONS.START,
    isLocal: true
  })
}

callEvents[actionTypes.MEDIA_RESTART_FINISH] = (action, params) => {
  return [
    callOperationHandler(action, {
      ...params,
      operation: OPERATIONS.MEDIA_RESTART,
      transition: OP_TRANSITIONS.FINISH,
      isLocal: true
    }),
    callEventHandler(eventTypes.MEDIA_RESTART, action, {
      error: action.payload.error
    })
  ]
}

// ----

callEvents[actionTypes.RESYNC] = (action, params) => {
  return callOperationHandler(action, {
    ...params,
    operation: OPERATIONS.RESYNC,
    transition: OP_TRANSITIONS.START,
    isLocal: true
  })
}

callEvents[actionTypes.RESYNC_FINISH] = (action, params) => {
  return [
    callOperationHandler(action, {
      ...params,
      operation: OPERATIONS.RESYNC,
      transition: OP_TRANSITIONS.FINISH,
      isLocal: true
    })
  ]
}

// ----

callEvents[actionTypes.SEND_CUSTOM_PARAMETERS] = (action, params) => {
  return callOperationHandler(action, {
    ...params,
    operation: OPERATIONS.SEND_CUSTOM_PARAMETERS,
    transition: OP_TRANSITIONS.START,
    isLocal: true
  })
}

callEvents[actionTypes.SEND_CUSTOM_PARAMETERS_FINISH] = (action, params) => {
  return callOperationHandler(action, {
    ...params,
    operation: OPERATIONS.SEND_CUSTOM_PARAMETERS,
    isLocal: true,
    transition: OP_TRANSITIONS.FINISH
  })
}

callEvents[actionTypes.CUSTOM_PARAMETERS_RECEIVED] = action => {
  return callEventHandler(eventTypes.CUSTOM_PARAMETERS, action, {
    customParameters: action.payload.customParameters
  })
}

// ----

callEvents[actionTypes.AVAILABLE_CODECS_RETRIEVED] = action => {
  return callEventHandler(eventTypes.AVAILABLE_CODECS, action, {
    kind: action.payload.kind,
    codecs: action.payload.codecs
  })
}

callEvents[actionTypes.PENDING_OPERATION] = (action, params) => {
  return callOperationHandler(action, {
    ...params,
    operation: action.payload.operation,
    transition: OP_TRANSITIONS.UPDATE,
    isLocal: true
  })
}

// TODO: Have a proper event for this since UPDATE_CALL does not change the call's state property.
callEvents[actionTypes.UPDATE_CALL] = (action, params) => {
  /*
   * Special-case: An "update call" operation is mostly likely an operation that
   *    we can't figure out what it is based on remote SDP change. In some cases,
   *    it will remove remote tracks (eg. MOH being removed from a call), so check
   *    to see if the action's remoteTracks is less than the prevCall's remoteTracks.
   *    If so, emit a "track removed" event as well.
   */
  const prevCall = getCallById(params.prevState, action.payload.id)

  let trackEvent
  if (prevCall) {
    const { remoteTracks: prevRemote } = prevCall
    const { remoteTracks = [] } = action.payload

    if (prevRemote.length > remoteTracks.length) {
      const removedTracks = without(remoteTracks, prevRemote)
      const payload = { id: prevCall.id, remoteTracks: removedTracks }
      trackEvent = trackRemovedHandler({ payload })
    }
  }

  return [trackEvent, stateChangeHandler(action, params)]
}

// ----

callEvents[webrtcActionTypes.SESSION_ICE_CONNECTION_STATE_CHANGE] = (action, context) => {
  const prevState = context.prevState
  const call = getCallByWebrtcSessionId(prevState, action.payload.id)

  if (call) {
    const prevMediaConnectionState = call.mediaConnectionState
    return callEventHandler(eventTypes.MEDIA_CONNECTION_CHANGE, action, {
      callId: call.id,
      previous: {
        state: prevMediaConnectionState
      }
    })
  }
}

export default callEvents
